import { Fragment, ReactNode, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Checkbox } from '@design-system';
import {
  getCumulativeLtcPayoutSeries,
  getMonthPayout,
} from '@shared/calculations/funding/projection/projectionTotals';
import { TooltipLabel } from '../TooltipLabel';
import { AggRenderContext, ColumnGroup, ColumnSpec, MonthRenderContext } from './types';

interface ProjectionTableProps {
  title?: string;
  subtitle?: ReactNode;
  toolbar?: ReactNode;
  groups: ColumnGroup[];
  months: MonthlyEntry[];
  openStateForFirstMonth: MonthlyState;
  // Cumulative LTC payout BEFORE the first month in this view, so the
  // running "paid to date" badge accounts for any payouts that happened
  // earlier in the projection (e.g. the EP lump row that triggered the
  // pre→payout split).
  cumulativeLtcPayoutBeforeFirstMonth?: number;
  footer?: ReactNode;
  /**
   * When true, months are grouped by `month.context.policyYear`. Each year
   * renders as one expandable summary row (aggregated via each column's
   * `renderAggregate`); expanding reveals the monthly rows beneath. A
   * "Total" footer row is always rendered in this mode.
   */
  groupByPolicyYear?: boolean;
}

interface YearGroup {
  policyYear: number;
  months: MonthlyEntry[];
  openStateAtStart: MonthlyState;
  cumulativeAtStart: number;
}

export function ProjectionTable({
  title,
  subtitle,
  toolbar,
  groups,
  months,
  openStateForFirstMonth,
  cumulativeLtcPayoutBeforeFirstMonth = 0,
  footer,
  groupByPolicyYear,
}: ProjectionTableProps) {
  const flatColumns = useMemo(() => groups.flatMap(g => g.columns), [groups]);

  const [yearlyView, setYearlyView] = useState(false);
  const groupingActive = Boolean(groupByPolicyYear) && yearlyView;

  // Running cumulative LTC payout per month — offset by anything paid before
  // this view started. `series[i]` is "paid through month i, inclusive".
  const cumulativeSeries = useMemo<number[]>(() => {
    const local = getCumulativeLtcPayoutSeries(months);
    return local.map(v => v + cumulativeLtcPayoutBeforeFirstMonth);
  }, [months, cumulativeLtcPayoutBeforeFirstMonth]);

  const yearGroups = useMemo<YearGroup[] | null>(() => {
    if (!groupingActive) return null;
    const result: YearGroup[] = [];
    let openState = openStateForFirstMonth;
    let cumulativeAtStart = cumulativeLtcPayoutBeforeFirstMonth;
    let cumulativeWalker = cumulativeLtcPayoutBeforeFirstMonth;
    months.forEach(month => {
      const last = result[result.length - 1];
      if (last && last.policyYear === month.context.policyYear) {
        last.months.push(month);
      } else {
        if (last) {
          openState = last.months[last.months.length - 1].state;
          cumulativeAtStart = cumulativeWalker;
        }
        result.push({
          policyYear: month.context.policyYear,
          months: [month],
          openStateAtStart: openState,
          cumulativeAtStart,
        });
      }
      cumulativeWalker += getMonthPayout(month);
    });
    return result;
  }, [months, openStateForFirstMonth, cumulativeLtcPayoutBeforeFirstMonth, groupingActive]);

  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const isExpanded = (year: number) => expandedYears.has(year);
  const toggleYear = (year: number) =>
    setExpandedYears(prev => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });

  const showHeaderBar = Boolean(title || toolbar || groupByPolicyYear);

  return (
    <div>
      {showHeaderBar && (
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
          <div>
            {title && <span className="font-medium text-gray-900">{title}</span>}
            {subtitle && <span className="ml-2 text-sm text-gray-500">{subtitle}</span>}
          </div>
          <div className="flex items-center gap-3">
            {groupByPolicyYear && (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700">
                <Checkbox
                  checked={yearlyView}
                  onCheckedChange={v => setYearlyView(v === true)}
                />
                Yearly view
              </label>
            )}
            {toolbar}
          </div>
        </div>
      )}
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20 bg-white shadow-[0_1px_0_rgba(0,0,0,0.08)]">
            <tr>
              {flatColumns.map(col => {
                const stickyClasses = col.sticky
                  ? 'sticky left-0 z-10 bg-white shadow-[1px_0_0_rgba(0,0,0,0.06)]'
                  : '';
                return (
                  <th
                    key={col.key}
                    className={`${stickyClasses} whitespace-nowrap border-r border-gray-200 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-700`}
                  >
                    <TooltipLabel label={col.label} tooltip={col.tooltip} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!yearGroups &&
              months.map((month, i) => {
                const openState = i === 0 ? openStateForFirstMonth : months[i - 1].state;
                return (
                  <DetailRow
                    key={month.context.monthIndex}
                    columns={flatColumns}
                    month={month}
                    openState={openState}
                    cumulativeLtcPayoutToDate={cumulativeSeries[i]}
                  />
                );
              })}
            {yearGroups &&
              yearGroups.map(group => {
                const expanded = isExpanded(group.policyYear);
                const lastIdxInGroup =
                  months.length - 1 -
                  [...months].reverse().findIndex(m => m.context.policyYear === group.policyYear);
                const cumulativeAtEnd = cumulativeSeries[lastIdxInGroup];
                return (
                  <Fragment key={group.policyYear}>
                    <SummaryRow
                      columns={flatColumns}
                      label={`Year ${group.policyYear}`}
                      months={group.months}
                      openStateAtStart={group.openStateAtStart}
                      cumulativeLtcPayoutAtEnd={cumulativeAtEnd}
                      expanded={expanded}
                      onToggle={() => toggleYear(group.policyYear)}
                    />
                    {expanded &&
                      group.months.map((month, i) => {
                        const openState =
                          i === 0 ? group.openStateAtStart : group.months[i - 1].state;
                        const globalIdx =
                          months.indexOf(group.months[0]) + i;
                        return (
                          <DetailRow
                            key={month.context.monthIndex}
                            columns={flatColumns}
                            month={month}
                            openState={openState}
                            cumulativeLtcPayoutToDate={cumulativeSeries[globalIdx]}
                            indent
                          />
                        );
                      })}
                  </Fragment>
                );
              })}
          </tbody>
          <tfoot>
            {groupByPolicyYear && months.length > 0 && (
              <TotalRow
                columns={flatColumns}
                months={months}
                openStateAtStart={openStateForFirstMonth}
                cumulativeLtcPayoutAtEnd={cumulativeSeries[cumulativeSeries.length - 1]}
              />
            )}
            {footer}
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function DetailRow({
  columns,
  month,
  openState,
  cumulativeLtcPayoutToDate,
  indent,
}: {
  columns: ColumnSpec[];
  month: MonthlyEntry;
  openState: MonthlyState;
  cumulativeLtcPayoutToDate: number;
  indent?: boolean;
}) {
  const ctx: MonthRenderContext = { month, openState, cumulativeLtcPayoutToDate };
  return (
    <tr className="border-b border-gray-100 bg-white hover:bg-gray-50">
      {columns.map((col, ci) => {
        const stickyClasses = col.sticky
          ? 'sticky left-0 z-[1] bg-white shadow-[1px_0_0_rgba(0,0,0,0.06)]'
          : '';
        const indentClass = indent && ci === 0 ? 'pl-8' : '';
        return (
          <td
            key={col.key}
            className={`${stickyClasses} ${indentClass} whitespace-nowrap border-r border-gray-100 px-2 py-1 align-middle text-gray-800`}
          >
            {col.render(ctx)}
          </td>
        );
      })}
    </tr>
  );
}

function SummaryRow({
  columns,
  label,
  months,
  openStateAtStart,
  cumulativeLtcPayoutAtEnd,
  expanded,
  onToggle,
}: {
  columns: ColumnSpec[];
  label: string;
  months: MonthlyEntry[];
  openStateAtStart: MonthlyState;
  cumulativeLtcPayoutAtEnd: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const ctx: AggRenderContext = {
    months,
    openStateAtStart,
    closeStateAtEnd: months[months.length - 1].state,
    cumulativeLtcPayoutAtEnd,
    label,
    kind: 'year',
  };
  return (
    <tr
      className="cursor-pointer border-y border-gray-200 bg-primary/5 hover:bg-primary/10"
      onClick={onToggle}
    >
      {columns.map((col, ci) => {
        const stickyClasses = col.sticky
          ? 'sticky left-0 z-[1] bg-primary/5 shadow-[1px_0_0_rgba(0,0,0,0.06)]'
          : '';
        return (
          <td
            key={col.key}
            className={`${stickyClasses} whitespace-nowrap border-r border-gray-100 px-2 py-3 align-middle text-gray-900`}
          >
            {ci === 0 ? (
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  className={`h-3.5 w-3.5 shrink-0 text-gray-700 transition-transform ${
                    expanded ? 'rotate-90' : ''
                  }`}
                />
                {col.renderAggregate ? col.renderAggregate(ctx) : <span>{label}</span>}
              </div>
            ) : col.renderAggregate ? (
              col.renderAggregate(ctx)
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function TotalRow({
  columns,
  months,
  openStateAtStart,
  cumulativeLtcPayoutAtEnd,
}: {
  columns: ColumnSpec[];
  months: MonthlyEntry[];
  openStateAtStart: MonthlyState;
  cumulativeLtcPayoutAtEnd: number;
}) {
  const ctx: AggRenderContext = {
    months,
    openStateAtStart,
    closeStateAtEnd: months[months.length - 1].state,
    cumulativeLtcPayoutAtEnd,
    label: 'Total',
    kind: 'total',
  };
  return (
    <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
      {columns.map(col => {
        const stickyClasses = col.sticky
          ? 'sticky left-0 z-[1] bg-gray-50 shadow-[1px_0_0_rgba(0,0,0,0.06)]'
          : '';
        return (
          <td
            key={col.key}
            className={`${stickyClasses} whitespace-nowrap border-r border-gray-200 px-2 py-2 align-middle text-gray-900`}
          >
            {col.renderAggregate ? col.renderAggregate(ctx) : <span className="text-gray-300">—</span>}
          </td>
        );
      })}
    </tr>
  );
}
